import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export interface EntityType {
  id: string;
  tenant_id: string;
  name: string;
  icon: string | null;
  default_metadata: Record<string, unknown>;
  allowed_parent_types: string[];
  is_system: boolean;
}

export interface Entity {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  entity_type_id: string;
  name: string;
  slug: string;
  is_active: boolean;
  metadata: Record<string, unknown>;
  // S3 — new optional fields
  code?: string | null;
  gps_lat?: number | null;
  gps_lng?: number | null;
  address?: string | null;
  status?: string;
  created_at: string;
  updated_at: string;
}

export interface EntityHealth {
  entity_id: string;
  // S3 — canonical fields
  total_cameras: number;
  cameras_online: number;
  cameras_offline: number;
  cameras_recording: number;
  uptime_pct: number | null;
  recording_pct: number | null;
  compliance_score: number | null;
  child_entity_count: number;
  last_updated: string;
  status: string;
  // legacy aliases (S12)
  cameras_total: number;
  cameras_unknown: number;
}

export interface EntityDevice {
  id: string;
  entity_id: string;
  device_type: string;
  device_id: string;
}

export interface EntityAttribute {
  key: string;
  value: unknown;
  value_type?: string;
}

export interface EntitySearchRow {
  id: string;
  name: string;
  code: string | null;
  status: string;
  gps_lat: number | null;
  gps_lng: number | null;
  entity_type_id: string | null;
  entity_type_name: string | null;
  parent_id: string | null;
  parent_name: string | null;
  camera_count: number;
  direct_child_count: number;
}

export interface MapEntityRow {
  id: string;
  name: string;
  code: string | null;
  entity_type_id: string | null;
  entity_type_name: string | null;
  entity_type_level: number;
  gps_lat: number;
  gps_lng: number;
  health: {
    uptime_pct: number | null;
    compliance_score: number | null;
    total_cameras: number;
    cameras_offline: number;
  };
}

export interface EntityRole {
  user_id: string;
  entity_id: string;
  role: string;
}

const entitiesKey = ["entities"] as const;
const entityTypesKey = ["entity-types"] as const;

export function useEntities() {
  return useQuery({
    queryKey: [...entitiesKey, "roots"],
    queryFn: async () => {
      const { data } = await api.get<Entity[]>("/api/v1/entities");
      return data;
    },
  });
}

export function useEntityTree(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "tree", entityId],
    queryFn: async () => {
      const { data } = await api.get<Entity[]>(
        `/api/v1/entities/${entityId}/tree`,
      );
      return data;
    },
    enabled: Boolean(entityId),
  });
}

// A31 — fetch the subtree for EACH root (not just roots[0]) so every root tree can
// expand. useQueries handles the dynamic root count without breaking rules-of-hooks.
export function useEntityTrees(entityIds: string[]) {
  return useQueries({
    queries: entityIds.map((id) => ({
      queryKey: [...entitiesKey, "tree", id],
      queryFn: async () => {
        const { data } = await api.get<Entity[]>(`/api/v1/entities/${id}/tree`);
        return data;
      },
      enabled: Boolean(id),
    })),
  });
}

export function useEntity(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "detail", entityId],
    queryFn: async () => {
      const { data } = await api.get<Entity>(`/api/v1/entities/${entityId}`);
      return data;
    },
    enabled: Boolean(entityId),
  });
}

export function useEntityAncestors(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "ancestors", entityId],
    queryFn: async () => {
      const { data } = await api.get<Entity[]>(
        `/api/v1/entities/${entityId}/ancestors`,
      );
      return data;
    },
    enabled: Boolean(entityId),
  });
}

export function useEntityHealth(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "health", entityId],
    queryFn: async () => {
      const { data } = await api.get<EntityHealth>(
        `/api/v1/entities/${entityId}/health`,
      );
      return data;
    },
    enabled: Boolean(entityId),
    // S3 — TanStack Query staleTime: 60s avoids hammering the
    // rollup endpoint while letting the user pull-to-refresh via
    // queryClient.invalidateQueries.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// S3 — entity search.
export function useEntitySearch(params: {
  q?: string;
  entity_type_id?: string;
  status?: string;
  parent_id?: string;
  has_cameras?: boolean;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.entity_type_id) qs.set("entity_type_id", params.entity_type_id);
  if (params.status) qs.set("status", params.status);
  if (params.parent_id) qs.set("parent_id", params.parent_id);
  if (params.has_cameras !== undefined)
    qs.set("has_cameras", String(params.has_cameras));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  const queryString = qs.toString();
  return useQuery({
    queryKey: [...entitiesKey, "search", queryString],
    queryFn: async () => {
      const { data } = await api.get<EntitySearchRow[]>(
        `/api/v1/entities/search${queryString ? "?" + queryString : ""}`,
      );
      return data;
    },
  });
}

// S3 — Map view: entities with GPS + batched health.
export function useMapEntities(params?: {
  entity_type_id?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.entity_type_id) qs.set("entity_type_id", params.entity_type_id);
  const queryString = qs.toString();
  return useQuery({
    queryKey: ["map", "entities", queryString],
    queryFn: async () => {
      const { data } = await api.get<MapEntityRow[]>(
        `/api/v1/map/entities${queryString ? "?" + queryString : ""}`,
      );
      return data;
    },
    staleTime: 60_000,
  });
}

// S3 — bulk import.
export interface BulkImportRowError {
  row: number;
  error: string;
}
export interface BulkImportResponse {
  imported_count: number;
  skipped_count: number;
  entities: { id: string; name: string; code: string }[];
}
export function useBulkImportEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<BulkImportResponse> => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<BulkImportResponse>(
        "/api/v1/entities/bulk-import",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entitiesKey });
    },
  });
}

export function useEntityDevices(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "devices", entityId],
    queryFn: async () => {
      const { data } = await api.get<EntityDevice[]>(
        `/api/v1/entities/${entityId}/devices`,
      );
      return data;
    },
    enabled: Boolean(entityId),
  });
}

export function useEntityAttributes(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "attrs", entityId],
    queryFn: async () => {
      const { data } = await api.get<EntityAttribute[]>(
        `/api/v1/entities/${entityId}/attributes`,
      );
      return data;
    },
    enabled: Boolean(entityId),
  });
}

export function useEntityRoles(entityId: string | undefined) {
  return useQuery({
    queryKey: [...entitiesKey, "roles", entityId],
    queryFn: async () => {
      const { data } = await api.get<EntityRole[]>(
        `/api/v1/entities/${entityId}/roles`,
      );
      return data;
    },
    enabled: Boolean(entityId),
  });
}

export function useEntityTypes() {
  return useQuery({
    queryKey: entityTypesKey,
    queryFn: async () => {
      const { data } = await api.get<EntityType[]>("/api/v1/entity-types");
      return data;
    },
  });
}

export function useCreateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      entity_type_id: string;
      parent_id: string | null;
      metadata?: Record<string, unknown>;
    }) => {
      const { data } = await api.post<Entity>("/api/v1/entities", input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entitiesKey });
    },
  });
}

export function useUpdateEntity(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Entity>) => {
      const { data } = await api.patch<Entity>(
        `/api/v1/entities/${id}`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entitiesKey });
    },
  });
}

export function useDeleteEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/entities/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entitiesKey });
    },
  });
}

export function useMoveEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; new_parent_id: string | null }) => {
      const { data } = await api.post<Entity>(
        `/api/v1/entities/${input.id}/move`,
        { new_parent_id: input.new_parent_id },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entitiesKey });
    },
  });
}

export function useAttachDevice(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { device_type: string; device_id: string }) => {
      const { data } = await api.post<EntityDevice>(
        `/api/v1/entities/${entityId}/devices`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "devices", entityId],
      });
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "health", entityId],
      });
    },
  });
}

export function useDetachDevice(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { device_type: string; device_id: string }) => {
      await api.delete(
        `/api/v1/entities/${entityId}/devices/${input.device_type}/${input.device_id}`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "devices", entityId],
      });
    },
  });
}

export function usePutAttribute(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; value: unknown }) => {
      const { data } = await api.put<EntityAttribute>(
        `/api/v1/entities/${entityId}/attributes/${encodeURIComponent(input.key)}`,
        { value: input.value },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "attrs", entityId],
      });
    },
  });
}

export function useDeleteAttribute(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      await api.delete(
        `/api/v1/entities/${entityId}/attributes/${encodeURIComponent(key)}`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "attrs", entityId],
      });
    },
  });
}

export function useAssignRole(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { user_id: string; role: string }) => {
      const { data } = await api.post<EntityRole>(
        `/api/v1/entities/${entityId}/roles`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "roles", entityId],
      });
    },
  });
}

export function useRevokeRole(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { user_id: string; role: string }) => {
      await api.delete(
        `/api/v1/entities/${entityId}/roles/${input.user_id}/${input.role}`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...entitiesKey, "roles", entityId],
      });
    },
  });
}

export function useCreateEntityType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      icon?: string | null;
      allowed_parent_types?: string[];
      default_metadata?: Record<string, unknown>;
    }) => {
      const { data } = await api.post<EntityType>(
        "/api/v1/entity-types",
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entityTypesKey });
    },
  });
}
